import React from 'react';
import { observer } from 'mobx-react';
import { observable, computed } from 'mobx';
import PropTypes from 'prop-types';
import { Api } from '@seedlang/stores';
import pluralize from 'pluralize';
import autobind from 'autobind-decorator';
import { UrlBuilder } from '@seedlang/models';
import { isPresent } from '@seedlang/utils';
import Spinner from 'components/spinner';
import styled from '@emotion/styled';
import Alert from 'components/alert';
import imageCompression from 'browser-image-compression';
import withLegend from 'components/form/with_legend';

const Wrapper = styled.div`
  background: white;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  input {
    margin: ${props => props.margin};
  }
`;

const Image = styled.div`
  background-size: cover;
  background-position: center center;
  width: 150px;
  height: 150px;
  border-radius: 50%;
  background-image: ${props => props.backgroundImage};
`;

const ImageWrapper = styled.div`
  position: relative;
  display: inline-block;
  .fa-times {
    position: absolute;
    right: 0;
    background: red;
    color: white;
    width: 25px;
    height: 25px;
    text-align: center;
    line-height: 24px;
    border-radius: 50%;
    font-size: 14px;
  }
  img {
    margin-top: 10px;
    max-width: 180px;
  }
`;

@observer
class InPlaceImageUpload extends React.Component {
  @observable showSpinner = false;
  @observable imageUrlFromResponse;
  @observable error = null;
  static propTypes = {
    onChange: PropTypes.func,
    value: PropTypes.string,
    model: PropTypes.string,
    querystring: PropTypes.object,
    margin: PropTypes.string,
  };

  static defaultProps = {
    margin: '0 0 20px 0',
  };

  @computed get imageUrl() {
    if (this.props.image) {
      return this.props.image.url;
    } if (this.imageUrlFromResponse) {
      return this.imageUrlFromResponse;
    }
    return null;
  }

  @autobind async onMultipartPost() {
    const file = this.refs.input.files[0];
    if (!file) return;

    const MAX_FILE_SIZE = 6 * 1024 * 1024; // 6MB in bytes
    const MAX_COMPRESSED_SIZE = 1024 * 1024; // 1MB in bytes

    if (file.size > MAX_FILE_SIZE) {
      this.error = 'File size exceeds 6MB limit. Please choose a smaller file.';
      this.refs.input.value = '';
      return;
    }

    this.error = null;
    this.showSpinner = true;

    try {
      let fileToUpload = file;

      if (file.size > MAX_COMPRESSED_SIZE) {
        const options = {
          maxSizeMB: 1,
          maxWidthOrHeight: 1920,
          useWebWorker: true,
        };
        const compressedBlob = await imageCompression(file, options);

        // Create a new File object from the compressed blob to maintain metadata
        fileToUpload = new File([compressedBlob], file.name, {
          type: file.type,
          lastModified: file.lastModified,
        });
      }

      Api.multipartPost({
        url: new UrlBuilder().build(pluralize(this.props.model), this.props.querystring),
        data: { data: this.data(fileToUpload) },
        successCallback: this.onChange,
      });
    } catch {
      this.error = 'Error processing image. Please try again.';
      this.showSpinner = false;
      this.refs.input.value = '';
    }
  }

  data(file = this.refs.input.files[0]) {
    const data = new FormData();
    data.append('blob', file);
    return data;
  }

  @autobind onDelete() {
    this.showSpinner = true;

    Api.destroy({
      url: `/api/${pluralize(this.props.model)}/${this.props.id || this.props.image.id}`,
      successCallback: this.onChange,
    });
  }

  @autobind onChange(resp) {
    this.showSpinner = false;
    if (resp.success) {
      if (this.props.updateCallback) {
        this.props.updateCallback(resp);
      }
    }

    // Clear the file input so that if the user selects the same file again, the input's onChange event will fire
    this.refs.input.value = '';

    if (this.props.onChange) {
      this.props.onChange(resp);
    }
  }

  render() {
    return (
      <Wrapper
        margin={this.props.margin}
      >
        {this.error && (
          <Alert
            margin='0 0 10px 0'
          >
            {this.error}
          </Alert>
        )}
        <input
          type='file'
          ref='input'
          onChange={this.onMultipartPost}
          accept='.jpg,.jpeg,.png,.gif'
          style={{ display: isPresent(this.imageUrl) ? 'none' : 'block' }}
        />
        {
          isPresent(this.imageUrl) &&
            <ImageWrapper>
              {
                !this.props.hideDelete &&
                  <i
                    className='fa fa-times fa-on-click delete-btn'
                    onClick={this.onDelete.bind(this)}
                  />
              }
              {
                !this.props.circle && <img alt='' src={this.imageUrl} />
              }
              {
                this.props.circle &&
                  <Image
                    onClick={this.onClick}
                    backgroundImage={`url(${this.imageUrl})`}
                  />
              }
            </ImageWrapper>
        }
        {
          this.showSpinner &&
            <Spinner
              className='blue'
              margin='0 auto'
            />
        }
      </Wrapper>
    );
  }
}

export default withLegend(InPlaceImageUpload);
